/**
 * Responsive topbar / drawer toggle.
 *
 * Narrow screens (max-width: 700px) hide the topbar buttons and surface
 * them inside a drawer that is opened by a hamburger button. Wide screens
 * keep the original inline layout — the hamburger and drawer chrome are
 * hidden by CSS, so calling this helper is a no-op there.
 *
 * The drawer container should already exist in the HTML and wrap the
 * buttons it owns. Existing element ids on those buttons are preserved
 * so that `main.ts` / `club.ts` can attach listeners without caring
 * about DOM position. This module only owns the open/close lifecycle.
 *
 * Lifecycle:
 *   - hamburger click → toggle .open on the drawer
 *   - click outside (drawer + hamburger) → close
 *   - Escape key → close
 *   - click on a button/link inside the drawer → close (so a tap on
 *     "退室" or "← Live に戻る" feels like a normal menu interaction
 *     and does not leave the drawer sitting open over the next view)
 *
 * The helper is intentionally idempotent and side-effect-free if the
 * element ids are missing (e.g. login screen before topbar is built).
 */
export function setupResponsiveNav(
  hamburgerId: string,
  drawerId: string,
): void {
  const hamburger = document.getElementById(hamburgerId);
  const drawer = document.getElementById(drawerId);
  if (!hamburger || !drawer) return;

  // `<html class="drawer-open">` を立てて、CSS 側で canvas / layout を
  // pointer-events: none にする。drawer 外 click が Phaser canvas や form
  // に pass-through する設計衝突を防ぐ (Game + Security review)。
  const open = (): void => {
    drawer.classList.add("open");
    document.documentElement.classList.add("drawer-open");
    hamburger.setAttribute("aria-expanded", "true");
  };
  const close = (): void => {
    drawer.classList.remove("open");
    // 他の drawer (= もう 1 ページ分の drawer) も閉じてから html class を外す
    const anyOpen = document.querySelector(".club-nav-drawer.open, .nav-drawer.open");
    if (!anyOpen) document.documentElement.classList.remove("drawer-open");
    hamburger.setAttribute("aria-expanded", "false");
  };
  const toggle = (): void => {
    if (drawer.classList.contains("open")) close();
    else open();
  };

  hamburger.setAttribute("aria-controls", drawerId);
  hamburger.setAttribute("aria-expanded", "false");

  hamburger.addEventListener("click", (e) => {
    e.stopPropagation();
    toggle();
  });

  // Tapping anything actionable inside the drawer dismisses it. We bind
  // on click bubbling so it runs *after* the original handler attached
  // by main.ts / club.ts (which were registered earlier on the buttons
  // by id, regardless of DOM position).
  drawer.addEventListener("click", (e) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    if (t.closest("button, a")) close();
  });

  document.addEventListener("click", (e) => {
    if (!drawer.classList.contains("open")) return;
    const t = e.target as Node | null;
    if (!t) return;
    if (drawer.contains(t) || hamburger.contains(t)) return;
    close();
  });

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && drawer.classList.contains("open")) close();
  });
}

// Auto-wire both pages. Each HTML hosts at most one pair of ids; missing
// ids are silently skipped so we can ship one bundle for both entries.
setupResponsiveNav("nav-hamburger", "nav-drawer");
setupResponsiveNav("club-nav-hamburger", "club-nav-drawer");
